import CloudSyncIcon from '@mui/icons-material/CloudSync';
import MemoryIcon from '@mui/icons-material/Memory';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SaveIcon from '@mui/icons-material/Save';
import StorageIcon from '@mui/icons-material/Storage';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { Box, Grid, LinearProgress, Link, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import useSystemStats from '../../../hooks/useSystemInfo';
import { formatBytes } from '../../../utils/common';
import AdminPostgresConnectionsDialog from './AdminPostgresConnectionsDialog';

const DataRow = ({ label, value }: { label: string; value: string | undefined }) => (
    <Grid item xs={12}>
        <Stack direction={'row'} spacing={0.5} alignItems={'center'} justifyContent={'space-between'}>
            <Typography variant="body2">{label}</Typography>
            <Typography variant="body2">{value}</Typography>
        </Stack>
    </Grid>
);

const AdminStatsBlock = () => {
    const { data: systemStats, isLoading: isSystemStatsLoading } = useSystemStats();
    const [postgresConnectionsDialogOpen, setPostgresConnectionsDialogOpen] = useState(false);

    const availableStoragePercentage = () => {
        if (!systemStats || !systemStats.storage) {
            return 0;
        }

        return 100 - Math.round((systemStats.storage.available / systemStats.storage.total) * 100);
    };

    const postgresConnectionUsagePercent = () => {
        if (!systemStats?.postgres) {
            return 0;
        }

        return systemStats.postgres.usagePercent;
    };

    return (
        <Box role="presentation" pb={2}>
            <ListItem>
                <ListItemIcon
                    sx={{
                        minWidth: '32px',
                    }}
                >
                    <SaveIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Storage" />
            </ListItem>

            <Stack direction={'column'} spacing={1} alignItems={'center'} pl={2} pr={2}>
                <Grid container spacing={0.5} alignItems={'center'}>
                    <Grid item xs="auto">
                        <Typography variant="body2">
                            {systemStats && systemStats.storage ? systemStats.storage.formatted.used : '0 GB'}
                        </Typography>
                    </Grid>
                    <Grid item xs>
                        <LinearProgress
                            variant={isSystemStatsLoading ? 'indeterminate' : 'determinate'}
                            value={availableStoragePercentage()}
                            color={availableStoragePercentage() > 90 ? 'error' : 'primary'}
                        />
                    </Grid>
                    <Grid item xs="auto">
                        <Typography variant="body2">
                            {systemStats && systemStats.storage ? systemStats.storage.formatted.total : '0 GB'} (
                            {availableStoragePercentage()}%)
                        </Typography>
                    </Grid>
                </Grid>
            </Stack>

            <ListItem>
                <ListItemIcon
                    sx={{
                        minWidth: '32px',
                    }}
                >
                    <StorageIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Postgres - Connections" />
            </ListItem>

            <Stack direction={'column'} spacing={1} alignItems={'center'} pl={2} pr={2}>
                <Grid container spacing={0.5} alignItems={'center'}>
                    <Grid item xs="auto">
                        <Typography variant="body2">
                            {systemStats?.postgres ? systemStats.postgres.totalConnections.toLocaleString() : '0'}
                        </Typography>
                    </Grid>
                    <Grid item xs>
                        <LinearProgress
                            variant={isSystemStatsLoading ? 'indeterminate' : 'determinate'}
                            value={postgresConnectionUsagePercent()}
                            color={postgresConnectionUsagePercent() > 80 ? 'error' : 'primary'}
                        />
                    </Grid>
                    <Grid item xs="auto">
                        <Typography variant="body2">
                            {systemStats?.postgres ? systemStats.postgres.maxConnections.toLocaleString() : '0'} (
                            {postgresConnectionUsagePercent()}%)
                        </Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={0.5} alignItems={'center'}>
                    <DataRow label="Health" value={systemStats?.postgres?.healthy ? 'Healthy' : 'Unavailable'} />
                    <DataRow label="Active" value={systemStats?.postgres?.activeConnections.toLocaleString()} />
                    <DataRow label="Idle" value={systemStats?.postgres?.idleConnections.toLocaleString()} />
                    <DataRow label="Idle In Transaction" value={systemStats?.postgres?.idleInTransactionConnections.toLocaleString()} />
                    <DataRow label="Waiting" value={systemStats?.postgres?.waitingConnections.toLocaleString()} />
                    <DataRow label="Read Pool Clients" value={systemStats?.postgres?.readPoolSize.toLocaleString()} />
                    <Grid item xs={12}>
                        <Link component="button" variant="body2" underline="hover" onClick={() => setPostgresConnectionsDialogOpen(true)}>
                            Connections Details
                        </Link>
                    </Grid>
                </Grid>
            </Stack>

            <AdminPostgresConnectionsDialog open={postgresConnectionsDialogOpen} onClose={() => setPostgresConnectionsDialogOpen(false)} />

            <ListItem>
                <ListItemIcon
                    sx={{
                        minWidth: '32px',
                    }}
                >
                    <CloudSyncIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="WsProxy - Status" />
            </ListItem>

            <Stack direction={'column'} spacing={1} alignItems={'center'} pl={2} pr={2}>
                <Grid container spacing={0.5} alignItems={'center'}>
                    <DataRow label="Live Mode" value={systemStats?.wsProxy.status?.liveMode ? 'Enabled' : 'Disabled'} />
                    <DataRow label="Active Connections" value={systemStats?.wsProxy.status?.activeConnections.toLocaleString()} />
                    <DataRow label="Open Connections" value={systemStats?.wsProxy.status?.openConnections.toLocaleString()} />
                    <DataRow label="Pending Connections" value={systemStats?.wsProxy.status?.pendingConnections.toLocaleString()} />
                    <DataRow label="Queue Size" value={systemStats?.wsProxy.status?.queueSize.toLocaleString()} />
                </Grid>
            </Stack>

            <ListItem>
                <ListItemIcon
                    sx={{
                        minWidth: '32px',
                    }}
                >
                    <MemoryIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Memory" />
            </ListItem>

            <Stack direction={'column'} spacing={1} alignItems={'center'} pl={2} pr={2}>
                <Grid container spacing={0.5} alignItems={'center'}>
                    <DataRow label="Memory Usage" value={formatBytes(systemStats?.wsProxy.status?.memory.rss || 0)} />
                    <DataRow label="Heap Total" value={formatBytes(systemStats?.wsProxy.status?.memory.heapTotal || 0)} />
                    <DataRow label="Heap Used" value={formatBytes(systemStats?.wsProxy.status?.memory.heapUsed || 0)} />
                    <DataRow label="External" value={formatBytes(systemStats?.wsProxy.status?.memory.external || 0)} />
                    <DataRow label="Array Buffers" value={formatBytes(systemStats?.wsProxy.status?.memory.arrayBuffers || 0)} />
                </Grid>
            </Stack>

            <ListItem>
                <ListItemIcon
                    sx={{
                        minWidth: '32px',
                    }}
                >
                    <SyncAltIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="WsProxy - Traffic" />
            </ListItem>

            <Stack direction={'column'} spacing={1} alignItems={'center'} pl={2} pr={2}>
                <Grid container spacing={0.5} alignItems={'center'}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color={'text.secondary'}>
                            Current Rate
                        </Typography>
                    </Grid>
                    <DataRow
                        label="Messages Per Minute"
                        value={systemStats?.wsProxy.stats?.currentRate.messagesPerMinute.toLocaleString()}
                    />
                    <DataRow
                        label="Bytes Received"
                        value={formatBytes(systemStats?.wsProxy.stats?.currentRate.bytesReceived || 0).toLocaleString()}
                    />
                    <DataRow
                        label="Bytes Sent"
                        value={formatBytes(systemStats?.wsProxy.stats?.currentRate.bytesSent || 0).toLocaleString()}
                    />
                </Grid>

                <Grid container spacing={0.5} alignItems={'center'}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color={'text.secondary'}>
                            Monthly Stats
                        </Typography>
                    </Grid>
                    <DataRow
                        label="Bytes Received"
                        value={formatBytes(systemStats?.wsProxy.stats?.monthlyStats.totalBytesReceived || 0).toLocaleString()}
                    />
                    <DataRow
                        label="Bytes Sent"
                        value={formatBytes(systemStats?.wsProxy.stats?.monthlyStats.totalBytesSent || 0).toLocaleString()}
                    />
                </Grid>
            </Stack>

            <ListItem>
                <ListItemIcon
                    sx={{
                        minWidth: '32px',
                    }}
                >
                    <RecordVoiceOverIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="TTS" />
            </ListItem>

            <Stack direction={'column'} spacing={1} alignItems={'center'} pl={2} pr={2}>
                <Grid container spacing={0.5} alignItems={'center'}>
                    <DataRow label="Fish Audio Credits" value={`$${systemStats?.tts?.fishAudioCredits?.toLocaleString() || 'N/A'}`} />
                    <DataRow label="TTS Health" value={systemStats?.tts?.health?.status || 'Unknown'} />
                    <DataRow label="TTS Rate Limiter" value={systemStats?.tts?.health?.rateLimiter.active ? 'On' : 'Off'} />
                    <DataRow label="TTS Rate Limiter Request ID" value={systemStats?.tts?.health?.rateLimiter.currentRequestId || 'None'} />
                    <DataRow
                        label="TTS Rate Limiter Queue Count"
                        value={systemStats?.tts?.health?.rateLimiter.queueCount.toLocaleString() || '0'}
                    />
                    <DataRow label="Last Request Age" value={moment(systemStats?.tts?.health?.timestamp || '0').fromNow()} />
                </Grid>
            </Stack>
        </Box>
    );
};

export default AdminStatsBlock;
